import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {ApiKey} from "crqlar-react-common/dist/models/identity/apiKey.model";
import {ApiKeyService} from "crqlar-react-common/dist/services/identity/apiKey.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const ApiKeysList = () => {
    useTrackScreenVisit('system api keys');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<ApiKey>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: apiKey => apiKey.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'key',
            header: t('Key'),
            accessorFn: apiKey => apiKey.apiSecretPub,
            cell: ({row}) => <Box>{row.original.apiSecretPub}</Box>,
            size: 320,
            meta: {defaultActive: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: apiKey => apiKey.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: apiKey => apiKey.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-api-keys',
        columns,
        getRowId: apiKey => apiKey.id!,
        onRowClick: apiKey => navigate(`/system/apiKeys/${apiKey.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, sorting}) => {
            const page = await ApiKeyService.getAllApiKeys({
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('API keys')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create API key')}
                            onClick={() => navigate('/system/apiKeys/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ApiKeysList;
